import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AudioLines,
  BookOpen,
  ChevronLeft,
  FilePenLine,
  Image as ImageIcon,
  Lightbulb,
  MoreHorizontal,
  Paperclip,
  Plus,
  Search,
  Telescope
} from 'lucide-react';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';
import { ACCEPTED_CHAT_FILE_ACCEPT } from '../../constants/acceptedChatFiles';

const MENU_GAP = 8;

export const CHAT_TOOL_KEYS = {
  IMAGE: 'image',
  THINK: 'think',
  SEARCH: 'search',
  CREATE_FILE: 'create_file',
  RESEARCH: 'research',
  STUDY: 'study'
};

export const ChatComposerPlusMenu = ({
  disabled = false,
  activeTool = null,
  onSelectTool,
  onAttachFiles
}) => {
  const { t } = useI18n();
  const reactId = useId();
  const fileInputId = `chat-composer-file-${reactId.replace(/:/g, '')}`;
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const fileInputRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    const trigger = buttonRef.current;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const isRtl = document.documentElement.dir === 'rtl';
    const nextStyle = {
      position: 'fixed',
      zIndex: 80,
      bottom: window.innerHeight - rect.top + MENU_GAP
    };

    if (isRtl) {
      nextStyle.right = Math.max(8, window.innerWidth - rect.right);
    } else {
      nextStyle.left = Math.max(8, rect.left);
    }

    setMenuStyle(nextStyle);
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMoreOpen(false);
      setMenuStyle(null);
      return undefined;
    }

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, moreOpen, updateMenuPosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event) => {
      if (
        rootRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target) ||
        fileInputRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selectTool = (key) => {
    onSelectTool?.(activeTool === key ? null : key);
    setOpen(false);
  };

  const mainItems = [
    {
      key: 'attach',
      label: t('chatToolAttach'),
      icon: Paperclip
    },
    {
      key: CHAT_TOOL_KEYS.IMAGE,
      label: t('chatToolImage'),
      icon: ImageIcon,
      onClick: () => selectTool(CHAT_TOOL_KEYS.IMAGE)
    },
    {
      key: CHAT_TOOL_KEYS.THINK,
      label: t('chatToolThink'),
      icon: Lightbulb,
      onClick: () => selectTool(CHAT_TOOL_KEYS.THINK)
    },
    {
      key: CHAT_TOOL_KEYS.SEARCH,
      label: t('chatToolSearch'),
      icon: Search,
      onClick: () => selectTool(CHAT_TOOL_KEYS.SEARCH)
    },
    {
      key: CHAT_TOOL_KEYS.CREATE_FILE,
      label: t('chatToolCreateFile'),
      icon: FilePenLine,
      onClick: () => selectTool(CHAT_TOOL_KEYS.CREATE_FILE)
    }
  ];

  const moreItems = [
    {
      key: CHAT_TOOL_KEYS.RESEARCH,
      label: t('chatToolResearch'),
      icon: Telescope,
      onClick: () => selectTool(CHAT_TOOL_KEYS.RESEARCH)
    },
    {
      key: CHAT_TOOL_KEYS.STUDY,
      label: t('chatToolStudy'),
      icon: BookOpen,
      onClick: () => selectTool(CHAT_TOOL_KEYS.STUDY)
    }
  ];

  const fileInput = (
    <input
      id={fileInputId}
      ref={fileInputRef}
      type="file"
      accept={ACCEPTED_CHAT_FILE_ACCEPT}
      multiple
      className="sr-only"
      disabled={disabled}
      onChange={(event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (files.length) {
          onAttachFiles?.(files);
        }
        setOpen(false);
      }}
    />
  );

  return (
    <div ref={rootRef} className="relative">
      {typeof document !== 'undefined' ? createPortal(fileInput, document.body) : fileInput}

      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className={classNames(
          'mb-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full transition disabled:opacity-40',
          open || activeTool
            ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-950'
            : 'text-neutral-500 hover:bg-neutral-200/70 hover:text-neutral-800'
        )}
        title={t('chatToolMore')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Plus size={18} />
      </button>

      {open && menuStyle && typeof document !== 'undefined'
        ? createPortal(
            <div ref={menuRef} className="flex items-end gap-2" style={menuStyle} role="menu">
              {moreOpen ? (
                <div className="min-w-[220px] overflow-hidden rounded-2xl border border-neutral-200 bg-[#F3F4F6] py-1 shadow-[0_12px_40px_rgba(0,0,0,0.08)] dark:border-border dark:bg-panel">
                  {moreItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTool === item.key;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        role="menuitem"
                        onClick={item.onClick}
                        className={classNames(
                          'flex w-full items-center gap-3 px-3.5 py-2.5 text-start text-sm transition hover:bg-white dark:hover:bg-accent-soft',
                          isActive ? 'font-semibold text-neutral-950 dark:text-text' : 'text-neutral-700 dark:text-muted'
                        )}
                      >
                        <Icon size={16} className="shrink-0 opacity-80" />
                        <span className="flex-1">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <div className="min-w-[240px] overflow-hidden rounded-2xl border border-neutral-200 bg-[#F3F4F6] py-1 shadow-[0_12px_40px_rgba(0,0,0,0.08)] dark:border-border dark:bg-panel">
                {mainItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTool === item.key;
                  const itemClassName = classNames(
                    'flex w-full items-center gap-3 px-3.5 py-2.5 text-start text-sm transition hover:bg-white dark:hover:bg-accent-soft',
                    isActive ? 'font-semibold text-neutral-950 dark:text-text' : 'text-neutral-700 dark:text-muted'
                  );

                  if (item.key === 'attach') {
                    return (
                      <label
                        key={item.key}
                        htmlFor={fileInputId}
                        role="menuitem"
                        className={classNames(
                          itemClassName,
                          disabled ? 'pointer-events-none cursor-not-allowed opacity-40' : 'cursor-pointer'
                        )}
                      >
                        <Icon size={16} className="shrink-0 opacity-80" />
                        <span className="flex-1">{item.label}</span>
                      </label>
                    );
                  }

                  return (
                    <button
                      key={item.key}
                      type="button"
                      role="menuitem"
                      onClick={item.onClick}
                      className={itemClassName}
                    >
                      <Icon size={16} className="shrink-0 opacity-80" />
                      <span className="flex-1">{item.label}</span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => setMoreOpen((value) => !value)}
                  className="flex w-full items-center gap-3 px-3.5 py-2.5 text-start text-sm text-neutral-700 transition hover:bg-white dark:text-muted dark:hover:bg-accent-soft"
                >
                  <MoreHorizontal size={16} className="shrink-0 opacity-80" />
                  <span className="flex-1">{t('chatToolMore')}</span>
                  <ChevronLeft size={14} className={classNames('opacity-50 transition', moreOpen && 'rotate-180')} />
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export const chatToolLabelKey = (tool) => {
  switch (tool) {
    case CHAT_TOOL_KEYS.IMAGE:
      return 'chatToolImage';
    case CHAT_TOOL_KEYS.THINK:
      return 'chatToolThink';
    case CHAT_TOOL_KEYS.SEARCH:
      return 'chatToolSearch';
    case CHAT_TOOL_KEYS.CREATE_FILE:
      return 'chatToolCreateFile';
    case CHAT_TOOL_KEYS.RESEARCH:
      return 'chatToolResearch';
    case CHAT_TOOL_KEYS.STUDY:
      return 'chatToolStudy';
    default:
      return null;
  }
};

export const ChatToolChip = ({ tool, onClear }) => {
  const { t } = useI18n();
  const labelKey = chatToolLabelKey(tool);
  if (!labelKey) return null;
  return (
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center gap-1.5 rounded-full bg-neutral-900 px-3 py-1 text-xs font-medium text-white dark:bg-white dark:text-neutral-950"
    >
      <AudioLines size={12} />
      {t(labelKey)}
      <span className="opacity-70">×</span>
    </button>
  );
};
